import { useState, useEffect } from 'react';
import { NavLink, Outlet, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useMMAuth } from '@machinemetrics/mm-react-tools';
import { DEFAULT_COMPANY, pickableCompanies } from '../constants/company';

const API_BASE = '/machinesim/api';

// Nav chunked into task-phase groups (Chunking + Law of Uniform Connectedness +
// Cognitive Load): a user reads the app's structure — watch the plant, plan the week,
// manage the data — instead of scanning a flat 7-item list.
//
// Monitor is ONE entry, not two. Dashboard and Live Floor were the same floor rendered twice from
// two different payloads, which let the same machine show a different status depending on which you
// opened; they are now one view at `/`.
const NAV_GROUPS = [
  { label: 'Monitor', items: [
    { to: '/',                label: 'Live Floor',       abbr: 'LF', end: true },
    { to: '/line',            label: 'Line Twin',        abbr: 'LT' },
    // Leaves the shell by design — Floor Control is the touch surface and mounts without the
    // sidebar, which would otherwise eat a third of an iPad portrait viewport.
    { to: '/control',         label: 'Floor Control',    abbr: 'FC' },
  ] },
  // One entry, not three. Sandbox / Flow / Schedule are consecutive stages of the same pipeline
  // (what could we do → how will it go → what's running), so they're tabs inside /plan rather than
  // three siblings that read as unrelated tools.
  { label: 'Plan', items: [
    { to: '/plan',            label: 'Plan',             abbr: 'PL' },
  ] },
  { label: 'Manage', items: [
    { to: '/erp',             label: 'ERP',              abbr: 'ER' },
    { to: '/companies',       label: 'Companies',        abbr: 'CO' },
    { to: '/stream/machine-types', label: 'HMI Studio',  abbr: 'HM' },
  ] },
];

const NAV_BOTTOM = [
  { to: '/settings', label: 'Settings', abbr: 'ST' },
];

const groupLabelStyle = {
  fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
  color: 'var(--cn-dimmed)', padding: '0 20px', marginBottom: 4,
};

const navItemStyle = (isActive, collapsed) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: collapsed ? '10px 0' : '10px 20px',
  justifyContent: collapsed ? 'center' : 'flex-start',
  fontSize: 13,
  fontWeight: isActive ? 600 : 500,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: isActive ? 'var(--cn-gold)' : 'var(--cn-muted)',
  background: isActive ? 'rgba(168,144,96,0.08)' : 'transparent',
  borderLeft: isActive ? '3px solid var(--cn-gold)' : '3px solid transparent',
  textDecoration: 'none',
  transition: 'color 0.15s, background 0.15s',
  whiteSpace: 'nowrap',
});

const abbrBox = (isActive) => ({
  width: 26, height: 26, flexShrink: 0, borderRadius: 6,
  display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 800, letterSpacing: '0.02em',
  background: isActive ? 'var(--cn-gold)' : 'rgba(168,144,96,0.12)',
  color: isActive ? '#1a1206' : 'var(--cn-muted)',
});

export default function ChentronixLayout() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('cn-nav-collapsed') === '1');

  const auth        = useMMAuth();
  const accessToken = auth?.accessToken ?? null;

  useEffect(() => { localStorage.setItem('cn-nav-collapsed', collapsed ? '1' : '0'); }, [collapsed]);

  useEffect(() => {
    if (!accessToken) return;
    const headers = { Authorization: `Bearer ${accessToken}` };
    const fetchCompanies = () =>
      axios.get(`${API_BASE}/companies`, { headers })
        .then(res => setCompanies(res.data.companies || []))
        .catch(() => {});
    fetchCompanies();
    const id = setInterval(fetchCompanies, 10000);
    return () => clearInterval(id);
  }, [accessToken]);

  const currentCompany = searchParams.get('company') || DEFAULT_COMPANY;
  // Always carry ?company= explicitly. This used to DROP the param when the slug was 'default',
  // which worked only while 'default' was also the UI's fallback. Now that the fallback is Rassini,
  // dropping it would silently bounce you to Rassini and make Chentronix unselectable — the same
  // "watching one company, looking at another's data" trap that cost us a session.
  const handleCompanyChange = (slug) => {
    setSearchParams({ ...Object.fromEntries(searchParams), company: slug });
  };

  const linkTo = (to) => ({ pathname: to, search: `?company=${currentCompany}` });

  const renderNav = (items) => items.map((item) => (
    <NavLink key={item.to} to={linkTo(item.to)} end={item.end} title={collapsed ? item.label : undefined}
      style={({ isActive }) => navItemStyle(isActive, collapsed)}>
      {({ isActive }) => collapsed
        ? <span style={abbrBox(isActive)}>{item.abbr}</span>
        : <>{item.label}</>}
    </NavLink>
  ));

  // Grouped nav: labels when expanded, a thin divider between groups when collapsed.
  const renderGroups = () => NAV_GROUPS.map((group, gi) => (
    <div key={group.label} style={{ marginBottom: collapsed ? 0 : 12 }}>
      {collapsed
        ? (gi > 0 && <div style={{ height: 1, background: 'rgba(168,144,96,0.12)', margin: '6px 14px' }} />)
        : <div style={groupLabelStyle}>{group.label}</div>}
      {renderNav(group.items)}
    </div>
  ));

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--cn-bg)' }}>
      {/* Sidebar */}
      <aside style={{
        width: collapsed ? 60 : 220,
        flexShrink: 0,
        background: 'var(--cn-surface)',
        borderRight: '1px solid rgba(168,144,96,0.12)',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'hidden',
      }}>
        {/* Brand + collapse toggle */}
        <div style={{ padding: collapsed ? '18px 0 14px' : '22px 20px 18px', borderBottom: '1px solid rgba(168,144,96,0.12)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: collapsed ? 'center' : 'space-between', gap: 8 }}>
            {!collapsed && (
              <div>
                <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'var(--cn-gold)', marginBottom: 6 }}>Chentronix</div>
                <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--cn-text)', lineHeight: 1.2 }}>Spork Factory</div>
              </div>
            )}
            <button
              onClick={() => setCollapsed(c => !c)}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{
                width: 26, height: 26, flexShrink: 0, borderRadius: 6, cursor: 'pointer',
                border: '1px solid rgba(168,144,96,0.25)', background: 'var(--cn-surface2)',
                color: 'var(--cn-muted)', fontSize: 14, lineHeight: 1, fontFamily: 'inherit',
              }}
            >
              {collapsed ? '»' : '«'}
            </button>
          </div>

          {/* Company selector (expanded only) — labelled so the dropdown has context (Proximity) */}
          {!collapsed && (
            <div style={{ marginTop: 14 }}>
              <label htmlFor="cn-company" style={{ ...groupLabelStyle, padding: 0, display: 'block', marginBottom: 5 }}>Company</label>
              <select id="cn-company" value={currentCompany} onChange={e => handleCompanyChange(e.target.value)}
                style={{ background: 'transparent', border: '1px solid rgba(168,144,96,0.3)', borderRadius: 4, color: 'var(--cn-text)', fontSize: 12, padding: '5px 8px', cursor: 'pointer', width: '100%' }}>
                {pickableCompanies(companies, currentCompany)
                  .map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Main nav */}
        <nav style={{ flex: 1, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: collapsed ? 4 : 0, overflowY: 'auto' }}>
          {renderGroups()}
        </nav>

        {/* Bottom nav */}
        <div style={{ borderTop: '1px solid rgba(168,144,96,0.08)', paddingTop: collapsed ? 6 : 0, paddingBottom: 8 }}>
          {renderNav(NAV_BOTTOM)}
        </div>
      </aside>

      {/* Content — pages get { collapsed, setCollapsed } for a Fullscreen/Focus mode */}
      <main style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
        <Outlet context={{ collapsed, setCollapsed }} />
      </main>
    </div>
  );
}
